export interface PostMsearchOptions {
  csrfToken: string;
  signal: AbortSignal;
}

/** POSTs a single-search msearch body to a search front door (`<host>/_msearch`)
 *  and returns its one response object. Throws on transport failure and on a
 *  per-query error inside a 200 envelope — a failed query is not "no results". */
export async function postMsearch<TResponse>(
  host: string,
  index: string,
  body: Record<string, unknown>,
  { csrfToken, signal }: PostMsearchOptions,
): Promise<TResponse> {
  const ndjson = `${JSON.stringify({ index })}\n${JSON.stringify(body)}\n`;
  const response = await fetch(`${host}/_msearch`, {
    method: "POST",
    headers: { "content-type": "application/x-ndjson", "X-CSRF-Token": csrfToken },
    credentials: "same-origin",
    body: ndjson,
    signal,
  });
  if (!response.ok) throw new Error(`msearch request failed: ${response.status}`);
  const json = await response.json();
  const result = json?.responses?.[0];
  if (!result || result.error) throw new Error("msearch response error");
  return result as TResponse;
}

// InstantSearch's `insideBoundingBox` is "neLat,neLng,swLat,swLng" (optionally ";"-joined boxes).
// Translate the first box into an OpenSearch `geo_bounding_box` on the geo field.
export function boundingBoxFilter(boundsBox: string, geoField: string): Record<string, unknown> | null {
  const firstBox = boundsBox.split(";")[0];
  if (!firstBox) return null;
  const nums = firstBox.split(",").map(Number);
  if (nums.length !== 4 || nums.some((n) => !Number.isFinite(n))) return null;
  const [neLat, neLng, swLat, swLng] = nums as [number, number, number, number];
  return {
    geo_bounding_box: {
      [geoField]: { top_left: { lat: neLat, lon: swLng }, bottom_right: { lat: swLat, lon: neLng } },
    },
  };
}

/** InstantSearch `refinementList` uiState as OpenSearch `terms` filters (attribute = field). */
export function refinementTermsFilters(refinementList: Record<string, string[]> | undefined | null) {
  const filters: Record<string, unknown>[] = [];
  if (refinementList) {
    for (const [field, values] of Object.entries(refinementList)) {
      if (values?.length) filters.push({ terms: { [field]: values } });
    }
  }
  return filters;
}
